import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CommentReportIcon } from "@roman/shared/components/Icons";
import { notion } from "@roman/shared/links/notion";
import type { ReportReason } from "@roman/shared/types/Comment";
import { useState } from "react";

type CommentReportModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  commentId: string;
  onReportSubmit: (reason: ReportReason) => void;
};

const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: "SexualContent", label: "性的な内容" },
  { value: "ViolentOrObjectionableContent", label: "暴力的または過激な内容" },
  { value: "DiscriminatoryOrOffensiveContent", label: "差別的または攻撃的な内容" },
  { value: "SpamOrMisleadingContent", label: "スパムや営利目的の宣伝" },
  { value: "HarassmentOrBullying", label: "嫌がらせ、いじめ" },
  { value: "Misinformation", label: "虚偽または誤った情報" },
];

export const CommentReportModal = ({
  open,
  setOpen,
  commentId: _commentId,
  onReportSubmit,
}: CommentReportModalProps) => {
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);

  const handleReasonSelect = (reason: ReportReason) => {
    setSelectedReason(reason);
  };

  const handleReportClick = () => {
    if (selectedReason) {
      onReportSubmit(selectedReason);
      // Reset state and close modal
      setSelectedReason(null);
      setOpen(false);
    }
  };

  const handleClose = () => {
    setSelectedReason(null);
    setOpen(false);
  };

  const handleReportGuidelines = () => {
    window.open(notion.reportGuidelines, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[360px] p-0 ring-0 ring-transparent [&>button]:hidden py-[30px] rounded-[10px]">
        <div className="flex flex-col bg-white">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-[20px] font-bold leading-[1.4] text-black tracking-[-0.01em]">報告</h2>
          </div>

          {/* Content */}
          <div className="px-[25px] mt-[20px]">
            <p className="text-[16px] leading-[30px] text-black text-center">以下から選択してください。</p>

            {/* Report reasons */}
            <div className="gap-[10px] mt-[30px] px-[23px]">
              {REPORT_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className="flex items-center cursor-pointer h-[40px]"
                  onClick={() => handleReasonSelect(reason.value)}
                >
                  <div className="relative mr-[12px]">
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason.value}
                      checked={selectedReason === reason.value}
                      onChange={() => handleReasonSelect(reason.value)}
                      className="sr-only"
                    />
                    {selectedReason === reason.value ? (
                      <svg width="20" height="20" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0 11.5C0 5.428 4.928 0.5 11 0.5C17.072 0.5 22 5.428 22 11.5C22 17.572 17.072 22.5 11 22.5C4.928 22.5 0 17.572 0 11.5ZM5.03883 12.5388L8.62623 16.1262C9.01675 16.5168 9.64992 16.5168 10.0404 16.1262L17.6287 8.53795C18.0183 8.1484 18.019 7.51677 17.6308 7.12584C17.2407 6.73296 16.6052 6.73144 16.2137 7.12294L9.33333 14.0033L6.44882 11.1269C6.05903 10.7382 5.42807 10.7386 5.03883 11.1278C4.64919 11.5175 4.64919 12.1492 5.03883 12.5388Z"
                          fill="#01A4F9"
                        />
                      </svg>
                    ) : (
                      <div className="w-[20px] h-[20px] rounded-full border-[1px] border-secondary" />
                    )}
                  </div>
                  <span className={"text-[16px] text-black font-medium leading-[30px] mb-[2px]"}>{reason.label}</span>
                </label>
              ))}
            </div>

            {/* Report guidelines button */}
            <div className="mt-[30px] w-full flex justify-center">
              <Button
                variant="ghost"
                onClick={handleReportGuidelines}
                className="w-[150px] h-[40px] bg-blue text-[#176BF2] flex items-center gap-2"
              >
                <CommentReportIcon />
                報告前に確認
              </Button>
            </div>

            {/* Action buttons */}
            <div className="mt-[40px]">
              <button
                type="button"
                onClick={handleReportClick}
                className={`text-[18px] leading-[35px] font-bold w-full h-[50px] py-0 bg-primary rounded-[10px] text-white ${!selectedReason ? "opacity-50" : ""}`}
              >
                報告する
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="w-full text-[#176BF2] underline font-regular mt-[24.5px]"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
